import { ACTIVITY_DOWNLOAD_TOAST_MESSAGES } from './reportDownloadToastConstants'
import { reportUserColumns } from '../reportUserColumns'
import { reportExportConfig } from '../reportExportConfig'
import { authFetch, BASE_BACKEND_URL } from '../../../../auth'
import { loadSessionField } from '../../../../auth/sessionProfileStore'
import { runFileDownload } from '../../../../components/downloads/fileDownloadStore'
import { TAB_ENDPOINTS, ReportTabLabels, ReportTabsMap } from '../reportTabs'
import { trackReportExportJob } from '../../../../realtime/reportExports/trackJob'

export function reportPresentationUrl(
  {
    activeTab,
    startDate,
    endDate,
    searchQuery = '',
    hasPendingSettlementQuery = '',
    includeBusiness = false
  },
  view
) {
  const query = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    search: searchQuery,
    view
  })
  const extra = new URLSearchParams(hasPendingSettlementQuery.replace(/^&/, ''))
  extra.forEach((value, key) => query.set(key, value))
  if (view === 'export' && includeBusiness) query.set('include_business', 'true')
  return `${BASE_BACKEND_URL}/reports/user_reports/presentation/${TAB_ENDPOINTS[activeTab]}?${query}`
}
export async function fetchReportPresentation(options, view, signal) {
  const response = await authFetch(reportPresentationUrl(options, view), { signal })
  if (response?.status === 404)
    throw new Error(
      'La exportación de reportes no está disponible en este servidor. Contacta a soporte para habilitarla.'
    )
  if (!response || !response.ok)
    throw new Error('No pudimos generar el reporte. Revisa tu conexión e intenta de nuevo.')
  return response
}
// POST keeps large custom selections out of URLs. The response contains at
// most 25 identities; totals and period roles cover the complete selection.
export async function fetchReportExportOptions(options, signal) {
  const response = await authFetch(
    `${BASE_BACKEND_URL}/reports/user_reports/options/${TAB_ENDPOINTS[options.activeTab]}`,
    {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        start_date: options.startDate,
        end_date: options.endDate,
        page: options.page || 1,
        per_page: 25,
        search: options.search || '',
        all_users: options.selection.allUsers,
        user_uids: options.selection.userUids.join(','),
        excluded_user_uids: options.selection.excludedUserUids.join(',')
      })
    }
  )
  if (!response?.ok) throw new Error('No se pudieron cargar los usuarios y actividades.')
  return response.json()
}

export function saveReportBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
function reportFilename(options, extension) {
  const label = ReportTabLabels[ReportTabsMap[options.activeTab]] || 'Resumen'
  const business = loadSessionField('business') || 'Negocio'
  return `ReporteUsuarios_${label}_${typeof business === 'string' ? business : business.name || 'Negocio'}_${options.startDate}_${options.endDate}.${extension}`.replace(
    /[\\/:*?"<>|]/g,
    '-'
  )
}
export function downloadReportTable(options) {
  const snapshot = { ...options }
  return runFileDownload({
    title: 'Tabla de usuarios',
    task: async (signal) => {
      const response = await fetchReportPresentation(snapshot, 'export', signal)
      const data = await response.json()
      if (!data.table?.length) return { empty: true }
      const columns = reportUserColumns(snapshot.activeTab, {
        multiDay: snapshot.startDate !== snapshot.endDate,
        isSingleDayRange: snapshot.startDate === snapshot.endDate
      })
      const escape = (value) => {
        const text = String(value ?? '—')
        const safe = /^[=+@\t\r-]/.test(text) ? `'${text}` : text
        return `"${safe.replace(/"/g, '""')}"`
      }
      const records = [
        columns.map((column) => column.label),
        ...data.table.map((row) =>
          columns.map((column) => (column.value ? column.value(row) : row[column.key]))
        )
      ]
      const blob = new Blob(
        ['\uFEFF' + records.map((row) => row.map(escape).join(',')).join('\r\n')],
        { type: 'text/csv;charset=utf-8' }
      )
      signal.throwIfAborted()
      const filename = reportFilename(snapshot, 'csv')
      saveReportBlob(blob, filename)
      return { filename }
    }
  })
}
export function downloadReportPdf(options) {
  const snapshot = { ...options, includeBusiness: true }
  return runFileDownload({
    title: 'Reporte de usuarios en PDF',
    task: async (signal) => {
      const response = await fetchReportPresentation(snapshot, 'export', signal)
      const data = await response.json()
      const { buildReportIndexPdf } = await import('./reportIndexPdfBuilder')
      const blob = await buildReportIndexPdf({ ...snapshot, data, signal })
      signal.throwIfAborted()
      const filename = reportFilename(snapshot, 'pdf')
      saveReportBlob(blob, filename)
      return { filename }
    }
  })
}
function activityExportBaseUrl(activeTab) {
  return `${BASE_BACKEND_URL}/reports/user_reports/activity_exports/${TAB_ENDPOINTS[activeTab]}`
}

function parseErrorBody(response) {
  return response
    .json()
    .catch(() => ({}))
    .then(
      (body) => body?.error || 'No pudimos generar el CSV. Revisa tu conexión e intenta de nuevo.'
    )
}

export function downloadReportSummary(options) {
  return downloadReportActivities({
    ...options,
    exportKind: 'summary',
    allUsers: true,
    userUids: [],
    activities: []
  })
}

export function downloadReportActivities(options) {
  const snapshot = {
    ...options,
    userUids: [...options.userUids],
    activities: [...options.activities],
    excludedUserUids: [...(options.excludedUserUids || [])]
  }
  const summary = snapshot.exportKind === 'summary'
  const config = reportExportConfig(snapshot.activeTab)
  const title = summary ? 'Resumen de usuarios en CSV' : `Reporte de ${config.name}`
  const usersLabel = snapshot.allUsers
    ? 'todos los usuarios'
    : snapshot.userUids.length === 1
      ? snapshot.userUids[0]
      : `${snapshot.userUids.length} usuarios`
  const rangeLabel =
    snapshot.startDate === snapshot.endDate
      ? snapshot.startDate
      : `${snapshot.startDate} al ${snapshot.endDate}`

  return runFileDownload({
    kind: 'activities',
    toastMessages: ACTIVITY_DOWNLOAD_TOAST_MESSAGES,
    title,
    context: {
      reportName: config.name,
      usersLabel,
      rangeLabel,
      activityCount: snapshot.activities.length
    },
    task: async (signal, reportProgress) => {
      const activeResponse = await authFetch(activityExportBaseUrl(snapshot.activeTab), {
        method: 'POST',
        signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: snapshot.startDate,
          end_date: snapshot.endDate,
          all_users: snapshot.allUsers,
          excluded_user_uids: snapshot.excludedUserUids.join(','),
          user_uids: snapshot.userUids.join(','),
          activities: snapshot.activities.join(','),
          ...(summary ? { export_kind: 'summary', search: snapshot.searchQuery || '' } : {})
        })
      })
      if (!activeResponse)
        throw new Error('No pudimos generar el CSV. Revisa tu conexión e intenta de nuevo.')
      if (!activeResponse.ok) {
        throw new Error(await parseErrorBody(activeResponse))
      }
      const job = await activeResponse.json()
      const { job_id: jobId, filename: plannedFilename } = job
      const fetchStatus = async () => {
        const statusResponse = await authFetch(
          `${activityExportBaseUrl(snapshot.activeTab)}/${jobId}?start_date=${encodeURIComponent(snapshot.startDate)}&end_date=${encodeURIComponent(snapshot.endDate)}`,
          { signal }
        )
        if (!statusResponse?.ok) {
          throw new Error('No pudimos generar el CSV. Revisa tu conexión e intenta de nuevo.')
        }
        return statusResponse.json()
      }
      const statusPayload = await trackReportExportJob({
        jobId,
        signal,
        realtimeEnabled: Boolean(job.realtime),
        fetchStatus,
        onProgress: reportProgress
      })
      if (statusPayload.status === 'empty') return { empty: true }
      if (statusPayload.status === 'error') {
        throw new Error(
          `${statusPayload.error || 'No pudimos generar el CSV.'} Referencia: ${jobId}`
        )
      }
      if (statusPayload.status !== 'done') {
        throw new Error('La descarga tardó demasiado. Intenta con un rango más corto.')
      }
      const fileResponse = await authFetch(
        `${activityExportBaseUrl(snapshot.activeTab)}/${jobId}/file?start_date=${encodeURIComponent(snapshot.startDate)}&end_date=${encodeURIComponent(snapshot.endDate)}`,
        { signal }
      )
      if (!fileResponse)
        throw new Error('No pudimos generar el CSV. Revisa tu conexión e intenta de nuevo.')
      if (fileResponse.status === 410) {
        throw new Error('El archivo ya no está disponible. Genera la descarga de nuevo.')
      }
      if (!fileResponse.ok) {
        throw new Error('No pudimos generar el CSV. Revisa tu conexión e intenta de nuevo.')
      }
      const blob = await fileResponse.blob()
      signal.throwIfAborted()
      const filename = statusPayload.filename || plannedFilename
      saveReportBlob(blob, filename)
      const rowCount = statusPayload.row_count ?? fileResponse.headers?.get('X-Report-Row-Count')
      return {
        filename,
        message: rowCount
          ? `${rowCount} ${summary ? (Number(rowCount) === 1 ? 'usuario' : 'usuarios') : Number(rowCount) === 1 ? 'actividad' : 'actividades'}.`
          : undefined
      }
    }
  })
}
